// Shared "add a participant" control — a tenant user picked from the team directory (internal)
// or a free-form outside contact (external). Same pattern MeetingForm.tsx uses for creating a
// general meeting; extracted here so AssessmentScheduleForm (and any future scheduling form)
// gets the identical, already-working behavior instead of a re-implementation.
//
// Matches app.domain.meetings.models.ParticipantIn exactly: an external row needs an email OR a
// phone (not necessarily both) — MeetingService only actually sends invites by email today (no
// SMS invite path), so a phone-only contact is a valid record staff can reach manually, it just
// won't get an automated notification yet.
import { useState } from 'react';
import { useListTenantUsersQuery } from '@/redux/internal-services/onboardingApi';
import { Button } from '@/components/UI/Button';

export interface ParticipantRow {
  key: string;
  type: 'internal' | 'external';
  user_id?: string;
  /** Set when this row is a known Contact rather than a free-typed outside person. Carried
   *  through to ParticipantIn.contact_id so the meeting participant stays LINKED to the CRM
   *  record — without it, seeding an auto-resolved property contact through this picker would
   *  silently downgrade a linked Contact into an unlinked name/email/phone. */
  contact_id?: string;
  name: string;
  email: string;
  phone: string;
  /** Why this row is here, when it was added automatically rather than by the user (e.g.
   *  "Sales Agent", "Owner — no primary contact set"). Shown as a quiet label next to the name
   *  so an auto-added participant explains itself instead of looking like somebody's choice.
   *  Rows the user adds by hand leave this unset. */
  origin?: string;
}

const inputCls =
  'w-full rounded-md border border-ui-border bg-ui-input px-2 py-0.5 !text-xs leading-tight text-ui-text font-poppins placeholder:!text-xs placeholder:text-ui-text3 focus:outline-none focus:ring-1 focus:ring-ui-ring/30';

interface ParticipantPickerProps {
  participants: ParticipantRow[];
  onChange: (rows: ParticipantRow[]) => void;
  label?: string;
}

export function ParticipantPicker({
  participants,
  onChange,
  label = 'Participants',
}: ParticipantPickerProps) {
  const [showAdd, setShowAdd] = useState(false);
  const [participantType, setParticipantType] =
    useState<'internal' | 'external'>('internal');
  const [userSearch, setUserSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [extName, setExtName] = useState('');
  const [extEmail, setExtEmail] = useState('');
  const [extPhone, setExtPhone] = useState('');
  const [extError, setExtError] = useState('');

  const { data: usersData } = useListTenantUsersQuery(
    participantType === 'internal' && showAdd
      ? { search: userSearch || undefined, page_size: 8 }
      : undefined,
    {
      skip: participantType !== 'internal' || !showAdd,
    },
  );

  const addInternal = (userId: string, name: string) => {
    if (participants.some((p) => p.user_id === userId)) return;

    onChange([
      ...participants,
      {
        key: `int-${userId}`,
        type: 'internal',
        user_id: userId,
        name,
        email: '',
        phone: '',
      },
    ]);

    setUserSearch('');
  };

  const addExternal = () => {
    if (!extEmail.trim() && !extPhone.trim()) {
      setExtError('An email or a phone number is required.');
      return;
    }

    setExtError('');

    onChange([
      ...participants,
      {
        key: `ext-${Date.now()}`,
        type: 'external',
        name: extName.trim(),
        email: extEmail.trim(),
        phone: extPhone.trim(),
      },
    ]);

    setExtName('');
    setExtEmail('');
    setExtPhone('');
  };

  const remove = (key: string) =>
    onChange(participants.filter((p) => p.key !== key));

  return (
    <div className="space-y-1">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <label className="block text-xs font-medium text-ui-text">
          {label}
        </label>

        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => setShowAdd(!showAdd)}
          className="h-6 md:h-7 min-h-0 px-1.5 py-0.5 text-xs"
        >
          + Add participant
        </Button>
      </div>

      {/* Existing participants */}
      {participants.length > 0 && (
        <div className="space-y-0.5">
          {participants.map((p) => (
            <div
              key={p.key}
              className="flex items-center justify-between gap-2 rounded-md border border-ui-border px-2 py-1"
            >
              <span className="truncate text-xs text-ui-text">
                {p.name || p.email || p.phone || 'Unknown'}
              </span>

              <button
                type="button"
                onClick={() => remove(p.key)}
                className="shrink-0 text-xs text-danger-text hover:underline"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add participant */}
      {showAdd && (
        <div className="border-t border-ui-border pt-1">
          {/* Toggle + close */}
          <div className="flex items-center justify-between mb-1">
            <div className="flex overflow-hidden rounded-md border border-ui-border">
              {(['internal', 'external'] as const).map((pt) => (
                <button
                  key={pt}
                  type="button"
                  onClick={() => { setParticipantType(pt); setExtError(''); }}
                  className={`px-2.5 py-0.5 text-xs font-medium transition-colors ${
                    participantType === pt
                      ? 'bg-accent-fill text-on-accent'
                      : 'text-ui-text3 hover:text-ui-text hover:bg-ui-subtle'
                  }`}
                >
                  {pt === 'internal' ? 'Team' : 'External'}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="p-0.5 text-ui-text3 transition-colors hover:text-ui-text"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {participantType === 'internal' && (
            <div className="relative">
              <input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                placeholder="Search team members..."
                className={inputCls}
              />
              {searchFocused && (usersData?.data ?? []).length > 0 && (
                <div className="absolute left-0 right-0 z-20 mt-0.5 max-h-28 overflow-y-auto [&::-webkit-scrollbar]:hidden rounded-md border border-ui-border bg-ui-card shadow-md">
                  {(usersData?.data ?? []).map((u) => (
                    <button
                      key={u.user_id}
                      type="button"
                      onClick={() => addInternal(u.user_id, u.name || u.email || 'Unknown')}
                      className="w-full px-2.5 py-1.5 text-left text-xs text-ui-text transition-colors hover:bg-accent-soft/30"
                    >
                      {u.name || u.email || 'Unknown'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {participantType === 'external' && (
            <div className="space-y-1">
              <div className="grid grid-cols-3 gap-1">
                <input value={extName} onChange={(e) => setExtName(e.target.value)} placeholder="Name" className={inputCls} />
                <input value={extEmail} onChange={(e) => setExtEmail(e.target.value)} placeholder="Email" className={inputCls} />
                <input value={extPhone} onChange={(e) => setExtPhone(e.target.value)} placeholder="Phone" className={inputCls} />
              </div>
              {extError && <p className="text-xs text-danger-text">{extError}</p>}
              <Button type="button" size="sm" variant="outline" onClick={addExternal} className="h-6 md:h-7 min-h-0 px-3 text-xs">
                Add Contact
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ParticipantPicker;
